import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Printer } from 'lucide-react';
import api, { productionAPI } from '@/lib/api';
import { printPdfBlob } from '@/lib/printPdf';

const DAYS_FR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

const addDays = (iso, n) => {
    const d = new Date(iso + 'T12:00:00');
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
};
const fmtDay = (iso) => {
    const d = new Date(iso + 'T12:00:00');
    return { name: DAYS_FR[d.getDay()], dm: `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}` };
};
const fmtShort = (iso) => {
    const d = new Date(iso + 'T12:00:00');
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export default function PrintWeekView({ month }) {
    const [wednesday, setWednesday] = useState(null);
    const [avail, setAvail] = useState(null); // { clients, days }
    const [loading, setLoading] = useState(true);
    const [printing, setPrinting] = useState(false);
    const [selected, setSelected] = useState(() => new Set());

    // Mercredis du mois (chaque semaine de production = mercredi → mardi suivant)
    const weeks = useMemo(() => {
        const [y, m] = month.split('-').map(Number);
        const res = [];
        const lastDay = new Date(y, m, 0).getDate();
        for (let day = 1; day <= lastDay; day++) {
            const d = new Date(y, m - 1, day, 12);
            if (d.getDay() === 3) res.push(d.toISOString().slice(0, 10));
        }
        return res;
    }, [month]);

    useEffect(() => {
        if (!weeks.length) { setWednesday(null); return; }
        const today = new Date().toISOString().slice(0, 10);
        const current = weeks.find(w => today >= addDays(w, -2) && today <= addDays(w, 6));
        setWednesday(current || weeks[0]);
        setSelected(new Set());
    }, [weeks]);

    // Matrice : du lundi (mer - 2j) au mardi suivant (mer + 6j) = 9 jours
    const days = useMemo(() => {
        if (!wednesday) return [];
        const monday = addDays(wednesday, -2);
        return Array.from({ length: 9 }, (_, i) => addDays(monday, i));
    }, [wednesday]);
    const weekDays = useMemo(() => days.slice(2), [days]); // mer → mar

    useEffect(() => {
        if (!days.length) return;
        setLoading(true);
        productionAPI.printAvailability(days[0], days[8])
            .then(res => setAvail(res.data))
            .catch(() => toast.error('Erreur de chargement des disponibilités'))
            .finally(() => setLoading(false));
    }, [days]);

    const clients = avail?.clients || [];
    const hasSheet = useCallback((date) => !!avail?.days?.[date]?.production, [avail]);
    const hasBl = useCallback((date, cid) => !!avail?.days?.[date]?.clients?.[cid], [avail]);

    const keySheet = (date) => `S|${date}`;
    const keyBl = (cid, date) => `B|${cid}|${date}`;

    const toggle = (key) => setSelected(prev => {
        const n = new Set(prev);
        n.has(key) ? n.delete(key) : n.add(key);
        return n;
    });
    // 2e clic sur un bouton rapide = action inverse (tout décocher pour ce bouton)
    const toggleMany = (keys) => setSelected(prev => {
        const n = new Set(prev);
        const allIn = keys.length > 0 && keys.every(k => n.has(k));
        keys.forEach(k => allIn ? n.delete(k) : n.add(k));
        return n;
    });

    const dayKeys = (date) => {
        const ks = [];
        if (hasSheet(date)) ks.push(keySheet(date));
        clients.forEach(c => { if (hasBl(date, c.id)) ks.push(keyBl(c.id, date)); });
        return ks;
    };
    const selectAllWeek = () => toggleMany(weekDays.flatMap(dayKeys));
    const selectDay = (date) => toggleMany(dayKeys(date));
    const selectClientWeek = (cid) => toggleMany(weekDays.filter(d => hasBl(d, cid)).map(d => keyBl(cid, d)));
    const selectSheetsWeek = () => toggleMany(weekDays.filter(hasSheet).map(keySheet));

    const doPrint = async (direct = false) => {
        // Ordre : toutes les fiches de production d'abord (par date), puis les BL regroupés par journée
        const items = [];
        days.forEach(date => {
            if (selected.has(keySheet(date))) items.push({ type: 'sheet', date });
        });
        days.forEach(date => {
            clients.forEach(c => {
                if (selected.has(keyBl(c.id, date))) items.push({ type: 'bl', date, client_id: c.id });
            });
        });
        if (!items.length) return;
        setPrinting(true);
        try {
            const res = await api.post('/production/print-batch', { items }, { responseType: 'blob' });
            if (direct) {
                await printPdfBlob(res.data);
            } else {
                window.open(URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })), '_blank');
            }
        } catch (e) {
            let detail = "Erreur lors de la génération";
            try { detail = JSON.parse(await e.response.data.text()).detail; } catch { /* ignore */ }
            toast.error(detail);
        } finally {
            setPrinting(false);
        }
    };

    if (!weeks.length) return <Card><CardContent className="py-10 text-center text-slate-400">Aucune semaine dans ce mois.</CardContent></Card>;

    return (
        <div className="space-y-3" data-testid="print-week-view">
            {/* Sélecteur de semaines */}
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Semaines :</span>
                {weeks.map(w => (
                    <button key={w} onClick={() => { setWednesday(w); setSelected(new Set()); }}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${wednesday === w ? 'bg-teal-700 text-white' : 'bg-white border text-slate-600 hover:text-slate-900'}`}
                        data-testid={`print-week-${w}`}>
                        Mer {fmtShort(w)} → Mar {fmtShort(addDays(w, 6))}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="py-12 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : (
                <Card>
                    <CardContent className="p-4 space-y-3 overflow-x-auto">
                        {/* Boutons rapides globaux */}
                        <div className="flex items-center gap-2 flex-wrap">
                            <Button size="sm" className="h-8 bg-teal-700 hover:bg-teal-800 text-white" onClick={selectAllWeek} data-testid="print-select-all-week">
                                Toute la semaine (mer → mar)
                            </Button>
                            <div className="ml-auto flex gap-2">
                                <Button className="btn-primary h-9" onClick={() => doPrint(false)} disabled={!selected.size || printing} data-testid="print-launch-btn">
                                    {printing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
                                    PDF ({selected.size})
                                </Button>
                                <Button variant="outline" className="h-9 hidden md:inline-flex" onClick={() => doPrint(true)} disabled={!selected.size || printing}
                                    title="Imprimer" data-testid="print-launch-direct-btn">
                                    <Printer className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>

                        <table className="border-collapse w-full text-xs" data-testid="print-matrix">
                            <thead>
                                <tr>
                                    <th className="border px-2 py-1.5 bg-slate-100 text-left w-44"></th>
                                    {days.map((d, i) => {
                                        const { name, dm } = fmtDay(d);
                                        const outside = i < 2;
                                        return (
                                            <th key={d} className={`border px-1 py-1.5 text-center ${outside ? 'bg-slate-50 text-slate-400' : 'bg-slate-100 text-slate-700'}`}>
                                                <div className="font-bold">{name}</div>
                                                <div className="font-normal">{dm}</div>
                                                <button onClick={() => selectDay(d)}
                                                    className={`mt-1 px-1.5 py-0.5 rounded text-white text-[10px] font-semibold ${outside ? 'bg-slate-400 hover:bg-slate-500' : 'bg-teal-600 hover:bg-teal-700'}`}
                                                    title="Cocher / décocher fiche + BL de ce jour"
                                                    data-testid={`print-day-btn-${d}`}>
                                                    Journée
                                                </button>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="bg-teal-50/50">
                                    <td className="border px-2 py-1.5 font-bold text-slate-800 whitespace-nowrap">
                                        <div className="flex items-center justify-between gap-2">
                                            <span>Fiche de production</span>
                                            <button onClick={selectSheetsWeek}
                                                className="px-1.5 py-0.5 rounded bg-teal-600 hover:bg-teal-700 text-white text-[10px] font-semibold shrink-0"
                                                title="Cocher les fiches du mercredi au mardi"
                                                data-testid="print-sheets-week-btn">
                                                Semaine
                                            </button>
                                        </div>
                                    </td>
                                    {days.map((d, i) => (
                                        <td key={d} className={`border text-center py-1.5 ${i < 2 ? 'bg-slate-50' : ''}`}>
                                            {hasSheet(d) ? (
                                                <Checkbox checked={selected.has(keySheet(d))} onCheckedChange={() => toggle(keySheet(d))}
                                                    data-testid={`print-cell-sheet-${d}`} />
                                            ) : <span className="text-slate-300">—</span>}
                                        </td>
                                    ))}
                                </tr>
                                {clients.filter(c => c.has_bl !== false).map(c => (
                                    <tr key={c.id}>
                                        <td className="border px-2 py-1.5 font-semibold text-slate-700 whitespace-nowrap">
                                            <div className="flex items-center justify-between gap-2">
                                                <span>BL {c.code}</span>
                                                <button onClick={() => selectClientWeek(c.id)}
                                                    className="px-1.5 py-0.5 rounded bg-slate-700 hover:bg-slate-800 text-white text-[10px] font-semibold shrink-0"
                                                    title={`Cocher tous les BL ${c.code} du mercredi au mardi`}
                                                    data-testid={`print-client-week-btn-${c.code}`}>
                                                    Semaine
                                                </button>
                                            </div>
                                        </td>
                                        {days.map((d, i) => (
                                            <td key={d} className={`border text-center py-1.5 ${i < 2 ? 'bg-slate-50' : ''}`}>
                                                {hasBl(d, c.id) ? (
                                                    <Checkbox checked={selected.has(keyBl(c.id, d))} onCheckedChange={() => toggle(keyBl(c.id, d))}
                                                        data-testid={`print-cell-bl-${c.code}-${d}`} />
                                                ) : <span className="text-slate-300">—</span>}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <p className="text-[11px] text-slate-400">
                            La semaine de production va du <strong>mercredi au mardi suivant</strong>. Les colonnes lundi/mardi (grisées, à gauche)
                            restent cochables manuellement au besoin. Seules les cases avec des quantités saisies sont cochables ; « — » = rien à imprimer.
                            L'impression génère un seul PDF : d'abord toutes les fiches de production (par date), puis les BL regroupés par journée.
                        </p>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
